import {PlaybackClock, StoryTelemetry} from './lz-story-telemetry.js';

export function storyStep(counts, product, clip, direction) {
  if (!counts.length || !counts[product] || ![-1, 1].includes(direction)) return null;
  const next = clip + direction;
  if (next >= 0 && next < counts[product]) return {product, clip: next};
  const adjacent = product + direction;
  if (adjacent < 0 || adjacent >= counts.length) return null;
  return {product: adjacent, clip: direction > 0 ? 0 : counts[adjacent] - 1};
}

export function purchaseSelection(product, variantId, quantity) {
  const variant = product?.variants?.find(item => String(item.id) === String(variantId));
  if (!variant || !variant.available || product.requires_selling_plan || product.variants.length >= 250) return {ok: false, reason: 'unavailable'};
  const rule = variant.quantity_rule || {min: 1, max: 99, increment: 1};
  const min = Math.max(1, Number(rule.min) || 1);
  const max = Math.min(99, Number(rule.max) || 99);
  const increment = Math.max(1, Number(rule.increment) || 1);
  const value = Number(quantity);
  if (!Number.isInteger(value) || value < min || value > max || (value - min) % increment) return {ok: false, reason: 'quantity_error'};
  return {ok: true, variant, quantity: value, rule: {min, max, increment}};
}

export function cartBody(selection) {
  if (!selection.ok) throw new Error('Invalid purchase selection');
  return {items: [{id: selection.variant.id, quantity: selection.quantity}]};
}

export function storyTimeline(counts) {
  return counts.flatMap((count, product) => Array.from({length: count}, (_, clip) => ({product, clip})));
}

export function lightStorySource(media) {
  return media?.sources?.filter(source => source.format === 'mp4' && source.width > 0 && source.height > 0 && source.width * source.height <= 1280 * 720).sort((a, b) => a.width * a.height - b.width * b.height)[0]?.url;
}

const BaseElement = globalThis.HTMLElement || class {};
export class LuzzartStories extends BaseElement {
  connectedCallback() {
    if (this.abort) return;
    this.abort = new AbortController();
    this.messages = Object.fromEntries([...this.querySelectorAll('[data-lzv-i18n] [data-key]')].map(node => [node.dataset.key, node.textContent]));
    this.products = [...this.querySelectorAll('[data-lzv-product]')].map(node => ({...JSON.parse(node.querySelector('[data-lzv-data]').textContent), node, clips: [...node.querySelectorAll('[data-lzv-clip]')]}));
    this.metrics = new StoryTelemetry({qa:this.dataset.metricsLocalQa === 'true',endpoint:this.dataset.metricsEndpoint,environment:this.dataset.metricsEnvironment,onReady:() => this.observeEntries()});
    this.experienceNumber = 0;this.playbackNumber = 0;
    this.counts = this.products.map(item => item.clips.length);
    this.timeline = storyTimeline(this.counts);
    this.$('progress').replaceChildren(...this.timeline.map(({product, clip}, index) => {
      const button = document.createElement('button');button.type = 'button';button.className = 'lzv__segment';button.dataset.lzvSegment = index;
      button.setAttribute('aria-label', `${this.t('story_of', {number:index+1,total:this.timeline.length})}: ${this.products[product].clips[clip].dataset.title}`);
      button.append(document.createElement('span'));return button;
    }));
    this.dialog = this.querySelector('dialog');
    this.p = -1;
    this.c = -1;
    this.muted = true;
    this.choices = new Map();
    this.autoAdvance = this.dataset.auto === 'true';
    this.compactViewport = matchMedia('(max-width: 749px)');
    this.landscapeViewport = matchMedia('(max-height: 500px) and (min-width: 600px)');
    const syncViewport = () => {
      if (!this.shopping || this.mobile()) return;
      this.setShopping(false, false);
      this.$('close').focus({preventScroll:true});
    };
    this.listen(this.compactViewport, 'change', syncViewport);
    this.listen(this.landscapeViewport, 'change', syncViewport);
    this.rootUrl = (this.dataset.root || '/').replace(/\/?$/, '/');
    this.money = new Intl.NumberFormat(document.documentElement.lang || 'pt-BR', {style: 'currency', currency: this.dataset.currency || 'BRL'});
    this.listen(this, 'click', event => this.onClick(event));
    this.listen(this.dialog, 'keydown', event => this.onKey(event));
    this.listen(this.dialog, 'cancel', event => {
      if (this.shopping) {event.preventDefault(); this.setShopping(false);}
    });
    this.listen(this.dialog, 'close', () => {if (!this.dialog.open) this.cleanup();});
    this.listen(this.dialog, 'click', event => {
      if (event.target !== this.dialog) return;
      const rect = this.dialog.getBoundingClientRect();
      if (event.clientX < rect.left || event.clientX > rect.right || event.clientY < rect.top || event.clientY > rect.bottom) this.dialog.close();
    });
    this.listen(this.$('variant'), 'change', () => {this.choices.set(this.active.id, this.$('variant').value);this.updateVariant();this.emit('variant_select');});
    this.listen(this.$('quantity'), 'input', () => this.updateVariant());
    this.listen(this.$('form'), 'submit', event => {event.preventDefault();this.addToCart();});
    this.listen(this.$('form'), 'focusin', () => {if (!this.mobile()) {this.userPaused = true;this.syncPlayback();}});
    this.listen(document, 'visibilitychange', () => {this.trackPlayback(true);this.hiddenPause = document.hidden;if (document.hidden) this.releasePrepared();this.syncPlayback();this.updateWidget();});
    this.listen(window, 'pagehide', () => {this.trackPlayback(true);this.releaseWidgetPreview();this.releasePrepared();});
    this.listen(navigator.connection, 'change', () => {if (!this.canPrepare()) this.releasePrepared();});
    this.listen(document, 'visitorConsentCollected', () => {
      if (!this.metrics.allowed()) this.metrics.reset();
      this.watchClock?.reset();this.watchCheckpoint = 0;this.qualified = false;
      this.playbackNumber++;this.observeEntries();this.updateWidget();
    });
    this.listen(this.$('screen'), 'pointerdown', event => this.pointerStart(event));
    this.listen(this.$('screen'), 'pointerup', event => this.pointerEnd(event));
    this.listen(this.$('screen'), 'pointercancel', () => this.cancelPointer());
    this.listen(this.$('rail'), 'scroll', () => this.updateRail());
    this.resizeObserver = new ResizeObserver(() => this.updateRail());
    this.resizeObserver.observe(this.$('rail'));
    this.updateRail();
    this.setupWidget();
    this.observeEntries();
    const sharedMedia = new URL(location.href).searchParams.get('lz_story');
    if (sharedMedia) {
      const productIndex = this.products.findIndex(item => item.clips.some(clip => clip.dataset.mediaId === sharedMedia));
      if (productIndex >= 0) this.open(productIndex, this.products[productIndex].node.querySelector('[data-lzv-open]'), this.products[productIndex].clips.findIndex(clip => clip.dataset.mediaId === sharedMedia), 'share');
    }
  }

  $(name) {return this.querySelector(`[data-lzv-${name}]`) || this.querySelector(`.lzv__${name}`);}
  listen(target, name, callback, options = {}) {target?.addEventListener(name, callback, {...options, signal: this.abort.signal});}
  t(key, values = {}) {return Object.entries(values).reduce((text, [name, value]) => text.replaceAll(`{{ ${name} }}`, String(value)), this.messages[key] || key);}
  get active() {return this.products[this.p];}
  get storyIndex() {return this.timeline.findIndex(item => item.product === this.p && item.clip === this.c);}
  mobile() {return this.compactViewport.matches && !this.landscapeViewport.matches;}
  format(value) {return this.money.format(Number(value || 0) / 100);}

  entryDetail(product, source) {
    const clip = product?.clips?.[0];
    return {source,productId:String(product?.id || this.dataset.pageProductId),productTitle:product?.title,productHandle:product?.handle,mediaId:clip?.dataset.mediaId,mediaTitle:clip?.dataset.title,variantId:null,durationMs:Number(clip?.dataset.duration || 0),experienceNumber:0,playbackNumber:0,watchMs:0};
  }
  observeEntries() {
    if (!this.isConnected || !this.products) return;
    this.exposure?.disconnect();
    this.exposure = new IntersectionObserver(entries => {
      for (const entry of entries) {
        if (!entry.isIntersecting || entry.intersectionRatio < .5 || document.hidden || this.dialog?.open) continue;
        const inline = entry.target === this.inlineEntry;
        const product = inline ? this.products.find(p => String(p.id) === this.dataset.pageProductId) : this.products.find(p => p.node.contains(entry.target));
        if (this.emit(inline ? 'gallery_impression' : 'impression',this.entryDetail(product,inline ? 'gallery' : 'rail'))) this.exposure.unobserve(entry.target);
      }
    }, {threshold:.5});
    this.products.forEach(product => this.exposure.observe(product.node.querySelector('[data-lzv-open]')));
    if (this.inlineEntry) this.exposure.observe(this.inlineEntry);
    this.widgetSeen = false;
  }

  updateRail() {
    const rail = this.$('rail');
    this.$('rail-prev').disabled = rail.scrollLeft < 5;
    this.$('rail-next').disabled = rail.scrollLeft + rail.clientWidth >= rail.scrollWidth - 5;
  }

  setupWidget() {
    this.widget = this.$('floating');
    this.gallery = document.querySelector('[data-lze-gallery]');
    const template = this.$('entry-template');
    if (!this.widget || !this.gallery || !template || this.gallery.querySelector('.lzv__entry')) return;
    this.widgetReady = true;
    this.widgetDismissed = false;
    try {this.widgetDismissed = sessionStorage.getItem('lzv:widget-dismissed') === '1';} catch {}
    this.inlineEntry = template.content.firstElementChild.cloneNode(true);
    this.inlineEntry.classList.add('lzv__entry--gallery');
    (this.gallery.querySelector('.lze-gal__stage') || this.gallery).append(this.inlineEntry);
    const inlineButton = this.inlineEntry.querySelector('button');
    inlineButton.dataset.lzvEntrySource = 'gallery';
    this.listen(inlineButton, 'click', () => this.openPageProduct(inlineButton));
    const floatingEntry = template.content.firstElementChild.cloneNode(true);
    floatingEntry.querySelector('button').dataset.lzvEntrySource = 'widget';
    this.widget.prepend(floatingEntry);
    this.widgetPreviewHost = floatingEntry.querySelector('.lzv__launch-image');
    this.reducedMotion = matchMedia('(prefers-reduced-motion: reduce)');
    this.listen(this.reducedMotion, 'change', () => this.syncWidgetPreview());
    this.listen(navigator.connection, 'change', () => this.syncWidgetPreview());
    this.purchasePanel = document.querySelector('[data-lze-panel]');
    this.widgetObstacles = [...document.querySelectorAll('.lze-sticky, #PBarNextFrameWrapper')];
    this.widgetPanels = [...document.querySelectorAll('cart-drawer, #MobileNavDrawer, .searchDrawer')];
    const schedule = () => {
      if (this.widgetFrame) return;
      this.widgetFrame = requestAnimationFrame(() => {this.widgetFrame = null;this.updateWidget();});
    };
    this.listen(window, 'scroll', schedule, {passive:true});
    this.listen(window, 'resize', schedule, {passive:true});
    this.listen(window, 'pageshow', schedule);
    [...this.widgetPanels, ...this.widgetObstacles].forEach(node => this.listen(node, 'transitionend', schedule));
    this.widgetResize = new ResizeObserver(schedule);
    [this.gallery, this.purchasePanel, ...this.widgetObstacles].filter(Boolean).forEach(node => this.widgetResize.observe(node));
    this.widgetMutation = new MutationObserver(schedule);
    [document.body, ...this.widgetObstacles, ...this.widgetPanels, ...document.querySelectorAll('dialog')].forEach(node => this.widgetMutation.observe(node, {attributes:true,attributeFilter:['class','style','open','hidden','aria-hidden']}));
    this.updateWidget();
  }

  openPageProduct(button) {
    const index = this.products.findIndex(item => String(item.id) === this.dataset.pageProductId);
    this.open(index, button, 0);
  }

  updateWidget() {
    if (!this.widgetReady) return;
    const height = window.innerHeight, width = document.documentElement.clientWidth;
    const visible = node => {
      if (!node || node.hidden) return false;
      const rect = node.getBoundingClientRect(), style = getComputedStyle(node);
      return style.visibility !== 'hidden' && style.display !== 'none' && Number(style.opacity) > 0 && Math.min(rect.bottom,height) - Math.max(rect.top,0) > 1 && Math.min(rect.right,width) - Math.max(rect.left,0) > 1;
    };
    const galleryPassed = this.gallery.getBoundingClientRect().bottom < 0;
    const overlay = [...document.querySelectorAll('dialog[open], [aria-modal="true"]'), ...this.widgetPanels].some(visible);
    const compact = this.compactViewport?.matches;
    if (this.inlineEntry) this.inlineEntry.hidden = Boolean(compact);
    const contentVisible = !galleryPassed || visible(this.purchasePanel) || visible(this.$('rail'));
    const suppressed = this.widgetDismissed || this.dialog.open || overlay || (!compact && contentVisible);
    let bottom = 16;
    for (const node of this.widgetObstacles) {
      if (visible(node)) bottom = Math.max(bottom, height - node.getBoundingClientRect().top + 12);
    }
    this.widget.style.setProperty('--lzv-widget-bottom', `${bottom}px`);
    this.widget.hidden = suppressed || height - bottom < 180;
    this.syncWidgetPreview();
    if (!this.widget.hidden && !this.widgetSeen && !document.hidden) this.widgetSeen = this.emit('widget_impression', this.entryDetail(this.products.find(p => String(p.id) === this.dataset.pageProductId),'widget'));
  }

  async syncWidgetPreview() {
    const allowed = this.widgetPreviewHost && !this.widget.hidden && !document.hidden && !this.dialog.open && this.compactViewport?.matches && !this.reducedMotion?.matches && !navigator.connection?.saveData;
    if (!allowed) {this.releaseWidgetPreview();return;}
    if (this.widgetPreview || this.widgetPreviewFailed || this.widgetPreviewRequest) return;
    const product = this.products.find(product => String(product.id) === this.dataset.pageProductId);
    if (!this.widgetPreviewSrc) {
      if (!product?.handle || !product.clips[0]) return;
      // Liquid can expose only the original; the Ajax catalog includes CDN renditions.
      const request = new AbortController();this.widgetPreviewRequest = request;
      const timer = setTimeout(() => {this.widgetPreviewFailed = true;request.abort();}, 8000);
      try {
        const response = await fetch(`${this.rootUrl}products/${encodeURIComponent(product.handle)}.js`, {signal:request.signal});
        if (!response.ok) throw new Error('Preview catalog unavailable');
        const catalog = await response.json();
        if (request.signal.aborted || this.widgetPreviewRequest !== request) return;
        this.rememberSources(catalog);
        this.widgetPreviewSrc = this.storySources?.get(product.clips[0].dataset.mediaId);this.widgetPreviewFailed = !this.widgetPreviewSrc;
      } catch {if (!request.signal.aborted) this.widgetPreviewFailed = true;}
      finally {clearTimeout(timer);if (this.widgetPreviewRequest === request) this.widgetPreviewRequest = null;}
      if (this.widgetPreviewSrc) return this.syncWidgetPreview();
      return;
    }
    // Decorative preview: never bind the player's playback clock or analytics events.
    const video = document.createElement('video');
    video.className = 'lzv__widget-preview';video.hidden = true;
    video.muted = true;video.defaultMuted = true;video.playsInline = true;video.loop = true;
    video.setAttribute('muted','');video.setAttribute('playsinline','');video.setAttribute('aria-hidden','true');video.tabIndex = -1;
    video.preload = 'none';video.src = this.widgetPreviewSrc;
    const controller = new AbortController();this.widgetPreviewAbort = controller;
    const failed = () => {if (this.widgetPreview !== video) return;this.widgetPreviewFailed = true;this.releaseWidgetPreview();};
    video.addEventListener('playing', () => {video.hidden = false;}, {signal:controller.signal});
    video.addEventListener('error', failed, {signal:controller.signal});
    this.widgetPreview = video;this.widgetPreviewHost.append(video);
    video.play().catch(failed);
  }

  releaseWidgetPreview() {
    this.widgetPreviewRequest?.abort();this.widgetPreviewRequest = null;
    this.widgetPreviewAbort?.abort();this.widgetPreviewAbort = null;
    if (!this.widgetPreview) return;
    this.widgetPreview.pause();this.widgetPreview.removeAttribute('src');this.widgetPreview.load();this.widgetPreview.remove();this.widgetPreview = null;
  }

  dismissWidget() {
    this.widgetDismissed = true;
    try {sessionStorage.setItem('lzv:widget-dismissed', '1');} catch {}
    this.updateWidget();
    this.emit('widget_dismiss', this.entryDetail(this.products.find(p => String(p.id) === this.dataset.pageProductId),'widget'));
  }

  onClick(event) {
    const button = event.target.closest('button');
    if (!button || !this.contains(button)) return;
    if (button.matches('[data-lzv-open]')) {this.open(this.products.findIndex(item => item.node.contains(button)), button);return;}
    const has = name => button.hasAttribute(`data-lzv-${name}`);
    if (has('launch')) this.openPageProduct(button);
    else if (has('dismiss')) this.dismissWidget();
    else if (has('close')) this.dialog.close();
    else if (has('skip')) this.step(1);
    else if (has('product-prev')) this.switchProduct(-1);
    else if (has('product-next')) this.switchProduct(1);
    else if (has('pause')) {if (!this.$('ended').hidden) {this.show(this.p,this.c);return;}this.userPaused = !this.video?.paused;this.syncPlayback();this.emit(this.userPaused ? 'pause' : 'resume');}
    else if (has('mute')) {this.muted = !this.muted; if (this.video) this.video.muted = this.muted;this.updateControls();}
    else if (has('retry') || has('replay')) this.show(this.p, this.c);
    else if (has('restart')) this.show(0, 0);
    else if (has('buy')) this.setShopping(true);
    else if (has('sheet-close')) this.setShopping(false);
    else if (has('continue')) {this.$('cart-result').hidden = true;this.$('form').hidden = false;this.setShopping(false);this.userPaused = false;this.syncPlayback();}
    else if (has('catalog-retry')) this.loadProduct();
    else if (has('share')) this.copyLink();
    else if (has('segment')) {
      const target = this.timeline[Number(button.dataset.lzvSegment)];
      if (target) this.show(target.product, target.clip);
    }
    else if (has('rail-prev') || has('rail-next')) this.$('rail').scrollBy({left: this.$('rail').clientWidth * (has('rail-next') ? .8 : -.8), behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'instant' : 'smooth'});
  }

  onKey(event) {
    if (this.shopping && event.key === 'Tab') {
      const nodes = [...this.$('shop').querySelectorAll('button:not(:disabled),a[href],input,select')].filter(node => node.getClientRects().length && !node.closest('[hidden]'));
      const first = nodes[0], last = nodes.at(-1);
      if (event.shiftKey && document.activeElement === first) {event.preventDefault();last?.focus();}
      else if (!event.shiftKey && document.activeElement === last) {event.preventDefault();first?.focus();}
    }
    if (event.target.matches('input,select,textarea') || this.shopping) return;
    if (event.key === 'ArrowRight' || event.key === 'ArrowLeft') {
      event.preventDefault(); const direction = event.key === 'ArrowRight' ? 1 : -1;
      if (event.altKey) this.switchProduct(direction); else this.step(direction);
    } else if (event.code === 'Space' && !event.target.closest('button,a')) {
      event.preventDefault();this.userPaused = !this.video?.paused;this.syncPlayback();
    }
  }

  open(index, trigger, clip = 0, source) {
    if (index < 0 || this.dialog.open || this.cartBusy) return;
    document.querySelectorAll('lz-product-videos dialog[open]').forEach(dialog => {dialog.close();dialog.closest('lz-product-videos')?.cleanup();});
    this.trigger = trigger;
    this.entrySource = source || trigger?.dataset?.lzvEntrySource || 'rail';
    this.experienceNumber++;
    this.scrollBefore = document.body.style.overflow;
    this.dialog.showModal();
    this.updateWidget();
    document.body.style.overflow = 'hidden';this.locked = true;
    this.shopping = false;this.hiddenPause = document.hidden;
    this.show(index, clip);
    this.emit('open');
  }

  show(productIndex, clipIndex) {
    if (this.cartBusy || !this.products[productIndex]?.clips[clipIndex]) return;
    const changed = productIndex !== this.p;
    const prepared = this.takePrepared(productIndex, clipIndex);
    this.releaseVideo();
    this.playbackNumber++;this.watchClock = new PlaybackClock();this.watchCheckpoint = 0;this.qualified = false;
    this.p = productIndex;this.c = clipIndex;this.userPaused = false;this.held = false;
    const template = this.active.clips[this.c];
    this.$('error').hidden = true;this.$('ended').hidden = true;this.hideLoading();
    this.$('title').textContent = template.dataset.title;
    const storyPosition = this.t('story_of', {number: this.storyIndex + 1, total: this.timeline.length});
    this.$('position').textContent = storyPosition;
    this.$('product-position').textContent = this.t('product_of', {number: this.p + 1, total: this.products.length});
    this.$('announcement').textContent = `${this.active.title}. ${storyPosition}`;
    this.$('skip').hidden = !storyStep(this.counts, this.p, this.c, 1);
    this.$('product-prev').disabled = this.p === 0;
    this.$('product-next').disabled = this.p === this.products.length - 1;
    [...this.$('progress').children].forEach((button, index) => {
      if (index === this.storyIndex) button.setAttribute('aria-current', 'step');else button.removeAttribute('aria-current');
      button.style.setProperty('--progress', index < this.storyIndex ? 1 : 0);
    });
    this.active.node.dataset.viewed = 'true';
    if (changed) {
      this.catalog = null;this.setShopping(false, false);this.$('product-title').textContent = this.active.title;
      this.updateImage(this.active.image);this.$('price').textContent = this.format(this.active.price);this.$('compare').hidden = true;
      this.$('quantity').value = '1';this.$('details').href = this.active.url;
      this.$('cart-result').hidden = true;this.$('form').hidden = false;
      this.loadProduct();this.emit('product_change');
    }
    const fragment = template.content.cloneNode(true);
    this.video = prepared || fragment.querySelector('video');
    if (!this.video) {this.showVideoError();return;}
    // Configure the detached element before any play request or source loading on iOS.
    this.video.controls = false;this.video.defaultMuted = this.muted;this.video.muted = this.muted;this.video.playsInline = true;
    if (this.muted) this.video.setAttribute('muted', '');else this.video.removeAttribute('muted');
    this.video.setAttribute('playsinline', '');this.video.preload = 'auto';
    const source = this.storySources?.get(template.dataset.mediaId) || (this.compactViewport?.matches && this.widgetPreviewSrc && String(this.active.id) === this.dataset.pageProductId && this.c === 0 ? this.widgetPreviewSrc : null);
    // A prepared element retains its decoder and buffer; assigning src again resets both.
    if (!prepared && source) this.video.src = source;
    this.video.removeAttribute('loading');
    this.video.setAttribute('aria-label', template.dataset.title);
    this.videoAbort = new AbortController();const signal = this.videoAbort.signal;
    this.progressMilestones = new Set();
    this.armBufferTimeout();
    this.video.addEventListener('playing', () => {this.trackPlayback();this.clearBufferTimeout();this.hideLoading();this.$('error').hidden = true;this.updateControls();}, {signal});
    this.video.addEventListener('playing', () => this.emit('play'), {signal, once:true});
    this.video.addEventListener('pause', () => {this.trackPlayback(true);this.updateControls();}, {signal});
    this.video.addEventListener('waiting', () => {
      if (!this.video.paused && this.expectsPlayback()) {this.queueLoading();this.armBufferTimeout();this.releasePrepared();}
    }, {signal});
    this.video.addEventListener('loadedmetadata', () => this.updateProgress(), {signal});
    this.video.addEventListener('timeupdate', () => {
      if (!this.video.paused && !this.video.seeking && this.video.readyState >= 3) {this.clearBufferTimeout();this.hideLoading();}
      this.updateProgress();this.prepareNext();
    }, {signal});
    const failedSources = new Set();
    this.video.addEventListener('error', event => {
      if (event.target.tagName === 'SOURCE') failedSources.add(event.target);
      if (event.target === this.video || failedSources.size >= this.video.querySelectorAll('source').length) this.showVideoError();
    }, {signal, capture:true});
    this.video.addEventListener('ended', () => {
      this.clearBufferTimeout();
      this.trackPlayback(true);
      this.emit('complete');
      if (this.autoAdvance && !this.shopping && !this.hiddenPause && !this.userPaused && storyStep(this.counts, this.p, this.c, 1)) this.step(1, false);
      else {this.$('ended').hidden = false;this.hideLoading();this.updateControls();}
    }, {signal});
    this.$('screen').replaceChildren(this.video);
    this.updateProgress();this.syncPlayback();
  }

  rememberSources(catalog) {
    this.storySources ||= new Map();
    for (const media of catalog.media || []) {const source = lightStorySource(media);if (source) this.storySources.set(String(media.id), source);}
  }
  canPrepare() {
    const connection = globalThis.navigator?.connection;
    return this.dialog.open && !this.hiddenPause && !this.shopping && !connection?.saveData && !['slow-2g','2g'].includes(connection?.effectiveType);
  }
  async prepareNext() {
    // Only after engagement and a healthy active buffer. Never warm the whole rail.
    const current = this.video;
    if (!this.canPrepare() || !this.expectsPlayback() || current.paused || current.readyState < 3 || current.currentTime < .75 || !current.buffered?.length) return;
    const lead = current.buffered.end(current.buffered.length - 1) - current.currentTime;
    if (lead < Math.min(2, current.duration - current.currentTime)) return;
    const next = storyStep(this.counts, this.p, this.c, 1);
    if (!next || this.prepared) return;
    const product = this.products[next.product], clip = product.clips[next.clip];
    const record = {...next, controller:new AbortController()};this.prepared = record;
    record.timer = setTimeout(() => {record.failed = true;record.controller.abort();}, 8000);
    try {
      let source = this.storySources?.get(clip.dataset.mediaId);
      if (!source) {
        const response = await fetch(`${this.rootUrl}products/${encodeURIComponent(product.handle)}.js`, {signal:record.controller.signal});
        if (!response.ok) throw new Error('Next video catalog unavailable');
        const catalog = await response.json();
        if (String(catalog.id) !== String(product.id)) throw new Error('Next video product mismatch');
        if (this.prepared !== record || record.controller.signal.aborted || !this.canPrepare()) return;
        this.rememberSources(catalog);source = this.storySources?.get(clip.dataset.mediaId);
      }
      if (!source || this.prepared !== record || !this.canPrepare()) return;
      // Paused, detached and without analytics listeners. One low-resolution MP4 only.
      const video = document.createElement('video');record.video = video;
      video.className = 'lzv__video';video.muted = true;video.defaultMuted = true;video.playsInline = true;
      video.preload = 'auto';video.src = source;
      video.addEventListener('error', () => {
        if (this.prepared !== record) return;
        record.failed = true;record.controller.abort();record.video = null;
        video.pause();video.removeAttribute('src');video.load();video.remove();
      }, {signal:record.controller.signal});
      video.load();
    } catch { /* Prefetch failure must never interrupt the current story. */ }
    finally {
      clearTimeout(record.timer);
      // Keep a failed record until navigation so timeupdate cannot retry continuously.
      if (this.prepared === record && !record.video) record.failed = true;
    }
  }
  takePrepared(product, clip) {
    const record = this.prepared;
    if (record?.product === product && record.clip === clip && record.video && !record.video.error) {
      this.prepared = null;clearTimeout(record.timer);record.controller.abort();return record.video;
    }
    this.releasePrepared();return null;
  }
  releasePrepared() {
    const record = this.prepared;this.prepared = null;
    if (!record) return;
    clearTimeout(record.timer);record.controller.abort();
    if (record.video) {record.video.pause();record.video.removeAttribute('src');record.video.load();record.video.remove();}
  }
  queueLoading() {
    if (this.loadingDelay || !this.$('loading').hidden) return;
    this.loadingDelay = setTimeout(() => {this.loadingDelay = null;if (this.expectsPlayback()) this.$('loading').hidden = false;}, 300);
  }
  hideLoading() {clearTimeout(this.loadingDelay);this.loadingDelay = null;this.$('loading').hidden = true;}

  step(direction, manual = true) {
    if (this.cartBusy || this.shopping) return;
    const next = storyStep(this.counts, this.p, this.c, direction);
    if (!next) return;
    if (manual) this.emit('skip', {direction});
    this.show(next.product, next.clip);
  }
  switchProduct(direction) {if (!this.shopping && !this.cartBusy) {this.emit('product_skip', {direction});this.show(this.p + direction, 0);}}
  updateProgress() {
    if (!this.video) return;
    this.trackPlayback();
    const duration = Number.isFinite(this.video.duration) ? this.video.duration : Number(this.active.clips[this.c].dataset.duration)/1000;
    const progress = duration > 0 ? Math.min(1, this.video.currentTime/duration) : 0;
    const segment = this.$('progress').children[this.storyIndex];segment?.style.setProperty('--progress', String(progress));
    for (const milestone of [25,50,75]) if (progress * 100 >= milestone && !this.progressMilestones.has(milestone)) {this.progressMilestones.add(milestone);this.emit('progress', {percent:milestone});}
  }
  trackPlayback(flush = false) {
    if (!this.video || !this.watchClock) return;
    if (!this.metrics?.allowed()) {this.watchClock.reset();return;}
    const watchMs = this.watchClock.sample({time:this.video.currentTime,now:performance.now(),playing:!this.video.paused && !this.video.seeking,visible:!document.hidden,rate:this.video.playbackRate});
    const duration = Number.isFinite(this.video.duration) ? this.video.duration * 1000 : Number(this.active?.clips[this.c]?.dataset.duration);
    if (!this.qualified && duration > 0 && watchMs >= Math.min(3000,duration)) {this.qualified = true;this.emit('qualified_view');}
    if (watchMs > this.watchCheckpoint && (flush || watchMs - this.watchCheckpoint >= 5000)) {this.watchCheckpoint = watchMs;this.emit('watch');}
  }
  updateControls() {
    const paused = this.video?.paused ?? true;
    this.$('pause').setAttribute('aria-label', this.t(paused ? 'play' : 'pause'));
    this.querySelector('[data-icon-play]').hidden = !paused;this.querySelector('[data-icon-pause]').hidden = paused;
    this.$('mute').setAttribute('aria-label', this.t(this.muted ? 'unmute' : 'mute'));
    this.querySelector('[data-icon-muted]').hidden = !this.muted;this.querySelector('[data-icon-sound]').hidden = this.muted;
    this.$('media').classList.toggle('is-paused', paused && this.$('loading').hidden && this.$('ended').hidden && this.$('error').hidden);
    this.$('screen').dataset.pausedLabel = this.t('paused');
  }
  syncPlayback() {
    if (!this.video || !this.dialog.open) return;
    const attempt = this.playAttempt = Symbol();
    if (!this.expectsPlayback()) {this.clearBufferTimeout();this.hideLoading();this.video.pause();}
    else {
      const video = this.video;
      if (video.readyState < 3) {this.queueLoading();this.armBufferTimeout();}
      video.play().catch(() => {if (video !== this.video || attempt !== this.playAttempt) return;this.clearBufferTimeout();this.hideLoading();if (video.error) this.showVideoError();else {this.userPaused = true;this.updateControls();}});
    }
    this.updateControls();
  }
  expectsPlayback() {return !!this.video && this.dialog.open && !this.userPaused && !this.shopping && !this.hiddenPause && !this.held && this.$('ended').hidden && this.$('error').hidden;}
  clearBufferTimeout() {clearTimeout(this.videoLoadTimer);this.videoLoadTimer = null;}
  armBufferTimeout() {
    if (this.videoLoadTimer) return;
    this.videoLoadTimer = setTimeout(() => {this.videoLoadTimer = null;if (this.expectsPlayback()) this.showVideoError();}, 20000);
  }
  showVideoError() {this.clearBufferTimeout();this.hideLoading();this.releasePrepared();this.$('error').hidden = false;this.video?.pause();this.updateControls();this.emit('error', {stage:'video'});}
  pointerStart(event) {
    if (event.button !== 0 || this.shopping) return;
    this.pointer = {x:event.clientX,y:event.clientY,id:event.pointerId};
    event.currentTarget.setPointerCapture(event.pointerId);
    this.holdTimer = setTimeout(() => {this.held = true;this.syncPlayback();}, 260);
  }
  pointerEnd(event) {
    if (!this.pointer) return;
    const {x,y} = this.pointer;const dx = event.clientX-x, dy = event.clientY-y;const held = this.held;
    this.cancelPointer();
    if (Math.abs(dx) > 55 && Math.abs(dx) > Math.abs(dy)) this.step(dx < 0 ? 1 : -1);
    else if (Math.abs(dy) > 70 && Math.abs(dy) > Math.abs(dx)) this.switchProduct(dy < 0 ? 1 : -1);
    else if (!held && Math.abs(dx) < 12 && Math.abs(dy) < 12) {
      const rect = this.$('screen').getBoundingClientRect();const position = (x-rect.left)/rect.width;
      if (position < .3) this.step(-1);else if (position > .7) this.step(1);else {this.userPaused = !this.video?.paused;this.syncPlayback();}
    }
  }
  cancelPointer() {clearTimeout(this.holdTimer);this.pointer = null;this.held = false;this.syncPlayback();}
  setShopping(value, focus = true) {
    this.shopping = value && this.mobile();
    if (this.shopping) this.releasePrepared();
    this.$('viewer').classList.toggle('is-shopping', this.shopping);
    this.$('media').inert = this.shopping;this.$('products-nav').inert = this.shopping;
    this.syncPlayback();
    if (focus) (this.shopping ? (this.$('variant').disabled ? this.$('sheet-close') : this.$('variant')) : this.$('buy')).focus({preventScroll:true});
    if (value) this.emit('shop_open');
  }

  async loadProduct() {
    const restoreFocus = document.activeElement === this.$('catalog-retry');
    this.productRequest?.abort();this.productRequest = new AbortController();
    const controller = this.productRequest, owner = this.active;
    this.$('catalog-status').textContent = this.t('loading_product');this.$('catalog-status').classList.remove('is-error');
    this.$('catalog-retry').hidden = true;this.$('add').disabled = true;this.$('variant').disabled = true;
    const timer = setTimeout(() => controller.abort(), 15000);
    try {
      const response = await fetch(`${this.rootUrl}products/${encodeURIComponent(owner.handle)}.js`, {signal:controller.signal,headers:{Accept:'application/json'},cache:'no-store'});
      if (!response.ok) throw new Error('Catalog request failed');
      const data = await response.json();
      if (String(data.id) !== String(owner.id) || !Array.isArray(data.variants)) throw new Error('Catalog product mismatch');
      if (controller !== this.productRequest || !this.dialog.open) return;
      this.catalog = data;
      this.rememberSources(data);
      const select = this.$('variant');select.replaceChildren();
      if (data.variants.length > 1) select.append(new Option(this.t('choose'), ''));
      for (const variant of data.variants) {
        const title = data.variants.length === 1 && variant.title === 'Default Title' ? data.title : variant.title;
        const option = new Option(`${title}${variant.available ? '' : ` — ${this.t('sold_out')}`}`, String(variant.id));option.disabled = !variant.available;select.append(option);
      }
      const stored = this.choices.get(owner.id);
      const queryVariant = owner.current ? new URL(location.href).searchParams.get('variant') : null;
      const candidate = stored || queryVariant;
      if (data.variants.some(variant => variant.available && String(variant.id) === candidate)) select.value = candidate;
      else if (data.variants.length > 1) select.value = '';
      select.disabled = false;
      const unsupported = data.requires_selling_plan || data.variants.length >= 250;
      this.$('form').hidden = !!unsupported;
      this.$('catalog-status').textContent = unsupported ? this.t('subscription') : '';
      this.updateVariant();
      if (restoreFocus) (unsupported ? this.$('details') : select).focus({preventScroll:true});
    } catch (error) {
      if (controller !== this.productRequest || !this.dialog.open) return;
      this.catalog = null;this.$('catalog-status').textContent = this.t('catalog_error');this.$('catalog-status').classList.add('is-error');
      this.$('catalog-retry').hidden = false;this.$('add').disabled = true;this.emit('error', {stage:'catalog'});
    } finally {clearTimeout(timer);}
  }

  updateVariant() {
    if (!this.catalog) return;
    const variant = this.catalog.variants.find(item => String(item.id) === this.$('variant').value);
    const price = variant?.price ?? this.catalog.price;
    this.$('price').textContent = this.format(price);
    const compare = variant?.compare_at_price;
    this.$('compare').hidden = !(compare > price);this.$('compare').textContent = compare > price ? this.format(compare) : '';
    this.$('details').href = `${this.active.url}${variant ? `?variant=${variant.id}` : ''}`;
    this.updateImage(variant?.featured_image?.src || this.active.image);
    const input = this.$('quantity');const rule = variant?.quantity_rule;
    input.min = String(rule?.min || 1);input.max = String(Math.min(99, rule?.max || 99));input.step = String(rule?.increment || 1);
    const selection = purchaseSelection(this.catalog, this.$('variant').value, input.value);
    this.$('add').disabled = this.cartBusy || !selection.ok;
    this.$('add-label').textContent = this.cartBusy ? this.t('adding') : this.t('add');
    if (!this.catalog.requires_selling_plan && this.catalog.variants.length < 250) this.$('catalog-status').textContent = !this.catalog.available || !this.catalog.variants.some(item => item.available) ? this.t('sold_out') : variant && !selection.ok ? this.t(selection.reason) : '';
  }

  updateImage(source) {
    const image = this.$('product-image');
    if (image.getAttribute('src') === source) return;
    image.style.visibility = 'hidden';
    image.onload = () => {image.style.visibility = 'visible';};
    image.onerror = () => {image.style.visibility = 'hidden';};
    image.src = source;
    if (image.complete && image.naturalWidth) image.style.visibility = 'visible';
  }

  async addToCart() {
    if (this.cartBusy) return;
    const selected = purchaseSelection(this.catalog, this.$('variant').value, this.$('quantity').value);
    if (!selected.ok) {this.$('catalog-status').textContent = this.t(selected.reason);return;}
    const owner = this.active, mediaId = owner.clips[this.c].dataset.mediaId;
    this.cartBusy = true;this.userPaused = true;this.syncPlayback();this.updateVariant();
    this.$('form').setAttribute('aria-busy','true');this.$('cart-result').hidden = true;
    this.$('variant').disabled = true;this.$('quantity').disabled = true;
    const controller = new AbortController();const timer = setTimeout(() => controller.abort(),20000);
    try {
      const body = cartBody(selected);
      const drawer = document.querySelector?.('cart-drawer');
      const sections = drawer ? (drawer.getSectionsToRender?.() || [{id:'cart-drawer',selector:'#CartDrawer'},{id:'cart-icon-bubble'}]) : null;
      if (sections?.length) {body.sections = sections.slice(0,5).map(section => section.id);body.sections_url = location.pathname;}
      const response = await fetch(`${this.rootUrl}cart/add.js`, {method:'POST',headers:{'Content-Type':'application/json',Accept:'application/json'},body:JSON.stringify(body),signal:controller.signal});
      const result = await response.json();
      if (!response.ok) {const error = new Error(this.t('cart_error'));error.confirmed = true;throw error;}
      if (!result.items?.some(item => String(item.variant_id || item.id) === String(selected.variant.id))) throw new Error('Unconfirmed cart response');
      try {this.syncThemeCart(drawer, sections, result);} catch {this.emit('error', {stage:'cart_display'});}
      this.emit('add_to_cart', {productId:String(owner.id),mediaId,variantId:String(selected.variant.id),quantity:selected.quantity,value:selected.variant.price*selected.quantity/100,currency:this.dataset.currency});
      document.dispatchEvent(new CustomEvent('luzzart:cart-updated', {detail:{source:'stories',items:result.items}}));
      if (this.isConnected && this.dialog.open && this.active.id === owner.id) {
        this.$('form').hidden = true;this.$('cart-result').hidden = false;this.$('cart-result').classList.remove('is-error');
        this.$('cart-message').textContent = this.t('added', {quantity:selected.quantity,title:owner.title});
        this.$('cart-link').focus({preventScroll:true});
      }
    } catch (error) {
      this.emit('error', {stage:'cart',productId:String(owner.id),variantId:String(selected.variant.id)});
      if (this.isConnected && this.dialog.open && this.active.id === owner.id) {
        this.$('cart-result').hidden = false;this.$('cart-result').classList.add('is-error');
        this.$('cart-message').textContent = this.t(error.confirmed ? 'cart_error' : 'cart_uncertain');
        this.$('cart-message').setAttribute('tabindex','-1');this.$('cart-message').focus({preventScroll:true});
      }
    } finally {
      clearTimeout(timer);this.cartBusy = false;
      if (this.isConnected) {this.$('form').removeAttribute('aria-busy');this.$('variant').disabled = !this.catalog;this.$('quantity').disabled = false;this.updateVariant();}
    }
  }

  syncThemeCart(drawer, sections, result) {
    if (!drawer || !sections?.length) return;
    const updates = sections.map(section => {
      const markup = result.sections?.[section.id];
      const target = section.selector ? document.querySelector(section.selector) : document.getElementById(section.id);
      const fragment = typeof markup === 'string' ? new DOMParser().parseFromString(markup,'text/html').querySelector(section.selector || '.shopify-section') : null;
      return {target,fragment};
    });
    if (updates.some(update => !update.target || !update.fragment)) throw new Error('Cart display response incomplete');
    for (const {target,fragment} of updates) target.innerHTML = fragment.innerHTML;
    drawer.classList.remove('is-empty');
  }

  async copyLink() {
    const url = new URL(this.active.url, location.origin);url.searchParams.set('lz_story',this.active.clips[this.c].dataset.mediaId);
    try {await navigator.clipboard.writeText(url.href);this.$('feedback').textContent = this.t('link_copied');this.emit('share');}
    catch {this.$('feedback').textContent = this.t('copy_error');}
  }
  emit(action, extra = {}) {
    const product = this.active || this.products?.find(item => String(item.id) === this.dataset.pageProductId);
    const clip = this.active?.clips[this.c];
    const detail = {action,productId:String(product?.id || this.dataset.pageProductId),productTitle:product?.title,productHandle:product?.handle,pageProductId:this.dataset.pageProductId,mediaId:clip?.dataset.mediaId || null,mediaTitle:clip?.dataset.title,variantId:this.$('variant')?.value || null,sectionId:this.dataset.sectionId,source:this.entrySource || 'rail',experienceNumber:this.experienceNumber,playbackNumber:this.playbackNumber,watchMs:Math.round(this.watchClock?.watchedMs || 0),durationMs:Number(clip?.dataset.duration || 0),...extra};
    this.dispatchEvent(new CustomEvent('luzzart:product-video', {bubbles:true,detail}));
    try {return this.metrics?.record(detail) || false;} catch {return false;}
  }
  releaseVideo() {
    this.trackPlayback(true);
    this.hideLoading();
    clearTimeout(this.holdTimer);this.clearBufferTimeout();this.pointer = null;this.videoAbort?.abort();
    if (this.video) {this.video.pause();this.video.removeAttribute('src');this.video.querySelectorAll('source').forEach(source => source.removeAttribute('src'));this.video.load();this.video.remove();this.video = null;}
  }
  cleanup() {
    if (this.p >= 0) this.emit('close');this.releaseVideo();this.releasePrepared();
    this.productRequest?.abort();this.productRequest = null;this.catalog = null;
    this.shopping = false;this.$('viewer')?.classList.remove('is-shopping');
    if (this.$('media')) this.$('media').inert = false;if (this.$('products-nav')) this.$('products-nav').inert = false;
    this.p = -1;this.c = -1;
    this.updateWidget();
    if (this.locked) {document.body.style.overflow = this.scrollBefore;this.locked = false;if (this.isConnected) this.trigger?.focus({preventScroll:true});}
  }
  disconnectedCallback() {this.widgetReady = false;this.releaseWidgetPreview();cancelAnimationFrame(this.widgetFrame);this.widgetResize?.disconnect();this.widgetMutation?.disconnect();this.inlineEntry?.remove();this.widget?.querySelector('.lzv__entry')?.remove();if (this.dialog?.open) this.dialog.close();this.cleanup();this.abort?.abort();this.abort = null;this.exposure?.disconnect();this.resizeObserver?.disconnect();}
}

if (globalThis.customElements && !customElements.get('lz-product-videos')) customElements.define('lz-product-videos', LuzzartStories);
